<template>
  <div class="user-management">
    <div class="page-header">
      <h2>用户管理</h2>
      <el-button type="primary" @click="showCreateDialog = true">
        <el-icon><Plus /></el-icon>
        添加用户
      </el-button>
    </div>

    <!-- 用户列表 -->
    <el-card>
      <el-table :data="users" v-loading="loading" stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="username" label="用户名" />
        <el-table-column prop="role" label="角色" width="100">
          <template #default="{ row }">
            <el-tag :type="row.role === 'admin' ? 'danger' : 'primary'">
              {{ row.role === 'admin' ? '管理员' : '普通用户' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'danger'">
              {{ row.status === 'active' ? '正常' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="last_login" label="最后登录" width="180">
          <template #default="{ row }">
            {{ row.last_login ? formatDate(row.last_login) : '从未登录' }}
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template #default="{ row }">
            <el-button size="small" @click="editUser(row)">编辑</el-button>
            <el-button 
              size="small" 
              type="danger" 
              @click="deleteUser(row)"
              :disabled="row.id === currentUser.id"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 创建用户对话框 -->
    <el-dialog 
      v-model="showCreateDialog" 
      title="添加用户" 
      width="500px"
      @close="resetForm"
    >
      <el-form :model="userForm" :rules="userRules" ref="userFormRef" label-width="80px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input 
            v-model="userForm.password" 
            type="password" 
            placeholder="请输入密码" 
            show-password
          />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="userForm.role" placeholder="请选择角色">
            <el-option label="管理员" value="admin" />
            <el-option label="普通用户" value="user" />
          </el-select>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <el-button @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" @click="createUser" :loading="submitting">
          确定
        </el-button>
      </template>
    </el-dialog>

    <!-- 编辑用户对话框 -->
    <el-dialog 
      v-model="showEditDialog" 
      title="编辑用户" 
      width="500px"
      @close="resetForm"
    >
      <el-form :model="userForm" :rules="editRules" ref="editFormRef" label-width="80px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input 
            v-model="userForm.password" 
            type="password" 
            placeholder="留空表示不修改密码" 
            show-password
          />
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="userForm.role" placeholder="请选择角色">
            <el-option label="管理员" value="admin" />
            <el-option label="普通用户" value="user" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="userForm.status" placeholder="请选择状态">
            <el-option label="正常" value="active" />
            <el-option label="禁用" value="inactive" />
          </el-select>
        </el-form-item>
      </el-form>
      
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="updateUser" :loading="submitting">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { authApi } from '../api'
import { auth } from '../utils/auth'

export default {
  name: 'UserManagement',
  components: {
    Plus
  },
  setup() {
    const users = ref([])
    const loading = ref(false)
    const submitting = ref(false)
    const showCreateDialog = ref(false)
    const showEditDialog = ref(false)
    const userFormRef = ref(null)
    const editFormRef = ref(null)
    const editingUserId = ref(null)

    const currentUser = computed(() => auth.getCurrentUser())

    const userForm = ref({
      username: '',
      password: '',
      role: 'user',
      status: 'active'
    })

    const userRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能少于 6 个字符', trigger: 'blur' }
      ],
      role: [
        { required: true, message: '请选择角色', trigger: 'change' }
      ]
    }

    const editRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
      ],
      password: [
        { min: 6, message: '密码长度不能少于 6 个字符', trigger: 'blur' }
      ],
      role: [
        { required: true, message: '请选择角色', trigger: 'change' }
      ],
      status: [
        { required: true, message: '请选择状态', trigger: 'change' }
      ]
    }

    // 获取用户列表
    const fetchUsers = async () => {
      try {
        loading.value = true
        const response = await authApi.getUsers()
        users.value = response.data
      } catch (error) {
        ElMessage.error('获取用户列表失败')
        console.error('获取用户列表失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 创建用户
    const createUser = async () => {
      try {
        await userFormRef.value.validate()
        submitting.value = true
        
        const response = await authApi.createUser(userForm.value)
        ElMessage.success('用户创建成功')
        showCreateDialog.value = false
        resetForm()
        fetchUsers()
      } catch (error) {
        if (error.response?.data?.message) {
          ElMessage.error(error.response.data.message)
        } else {
          ElMessage.error('创建用户失败')
        }
        console.error('创建用户失败:', error)
      } finally {
        submitting.value = false
      }
    }

    // 编辑用户
    const editUser = (user) => {
      editingUserId.value = user.id
      userForm.value = {
        username: user.username,
        password: '',
        role: user.role,
        status: user.status
      }
      showEditDialog.value = true
    }

    // 更新用户
    const updateUser = async () => {
      try {
        await editFormRef.value.validate()
        submitting.value = true
        
        const updateData = { ...userForm.value }
        if (!updateData.password) {
          delete updateData.password
        }
        
        await authApi.updateUser(editingUserId.value, updateData)
        ElMessage.success('用户更新成功')
        showEditDialog.value = false
        resetForm()
        fetchUsers()
      } catch (error) {
        if (error.response?.data?.message) {
          ElMessage.error(error.response.data.message)
        } else {
          ElMessage.error('更新用户失败')
        }
        console.error('更新用户失败:', error)
      } finally {
        submitting.value = false
      }
    }

    // 删除用户
    const deleteUser = async (user) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除用户 "${user.username}" 吗？此操作不可恢复。`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        await authApi.deleteUser(user.id)
        ElMessage.success('用户删除成功')
        fetchUsers()
      } catch (error) {
        if (error !== 'cancel') {
          if (error.response?.data?.message) {
            ElMessage.error(error.response.data.message)
          } else {
            ElMessage.error('删除用户失败')
          }
          console.error('删除用户失败:', error)
        }
      }
    }

    // 重置表单
    const resetForm = () => {
      userForm.value = {
        username: '',
        password: '',
        role: 'user',
        status: 'active'
      }
      editingUserId.value = null
      if (userFormRef.value) userFormRef.value.resetFields()
      if (editFormRef.value) editFormRef.value.resetFields()
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    }

    onMounted(() => {
      fetchUsers()
    })

    return {
      users,
      loading,
      submitting,
      showCreateDialog,
      showEditDialog,
      userFormRef,
      editFormRef,
      userForm,
      userRules,
      editRules,
      currentUser,
      fetchUsers,
      createUser,
      editUser,
      updateUser,
      deleteUser,
      resetForm,
      formatDate
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}
</style>
